import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, Bot, CheckCircle2, CircleDashed, Clock, ExternalLink, Filter, MessageCircle, MessagesSquare, RotateCw, Send, ShieldCheck, Workflow, Zap, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { trackOperation } from "@/app/live";
import { IntentChip, SentimentChip } from "@/components/labels";
import { Badge, type Tone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { PanelSection, QuickPanel } from "@/components/ui/quick-view";
import { ErrorState } from "@/components/ui/states";
import { TechnicalDetails } from "@/components/ui/technical-details";
import { PostThumb } from "@/features/content/post-quick";
import { useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { invalidate } from "@/lib/api/query-client";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { usePeek } from "@/lib/peek";
import type { AutomationRun, RunDetail, RunStep } from "@/lib/types";
import { sendErrorInfo } from "@/lib/reauth";
import { cn, uuid } from "@/lib/utils";
import { PersonLink } from "@/features/people360/person-link";
import { RunCommentStatus } from "./run-comment-status";

export const RESULT_TONE: Record<AutomationRun["result"], Tone> = { executed: "ok", pending_approval: "warn", suggested: "violet", skipped: "neutral", failed: "bad" };

/** Retry one failed run (202 + operation toast). Each click carries its own Idempotency-Key, so a network retry can't double it. */
export function useRetryRun() {
  const { t } = useT();
  return useApiMutation("retryRun", {
    onSuccess: (op) => {
      trackOperation(op.id, { label: t("quick.run.retrying"), done: t("quick.run.retryDone"), onDone: () => void invalidate("runs", "run", "rulesHealth") });
      void invalidate("run");
    },
    onError: (e) => toast.error(t("quick.run.retryFailed"), { description: e.problem?.detail ?? e.problem?.title }),
  });
}

/** The API's generic text for a failed run nothing recorded a reason for (never shown as detail). */
const GENERIC_RUN_ERROR = "the action failed";

/**
 * Why a failed run failed, like the jobs center (QA F6): the localized message of its stable `errorCode`
 * (`messaging_window_closed` → "The 24-hour window is closed…") plus the raw reason as detail when it adds something.
 * A run without a typed reason reads "The action failed", never Temporal's wrapper text.
 */
export function runErrorInfo(x: { error?: string | null; errorCode?: string | null }, t: ReturnType<typeof useT>["t"]): { message: string; detail: string | null } | null {
  if (!x.error && !x.errorCode) return null;
  const info = sendErrorInfo(x);
  const raw = x.error?.trim() ?? "";
  if (info.code === "unknown") return { message: t("quick.run.actionFailed"), detail: info.detail && raw.toLowerCase() !== GENERIC_RUN_ERROR ? info.detail : null };
  return { message: info.message, detail: info.detail };
}

export const retryArgs = (runId: string) => ({ params: { runId }, body: {}, headers: { "Idempotency-Key": `run-retry.${runId}.${uuid()}` } });

/** Bulk retry: per-run results, failed ones queued, the rest reported as skipped. */
export function useRetryRuns() {
  const { t } = useT();
  return useApiMutation("retryRuns", {
    onSuccess: (r) => {
      toast.success(t("quick.run.retryBulkDone", { queued: r.queued.length, skipped: r.skipped.length }), { description: r.skipped[0]?.reason });
      for (const q of r.queued) trackOperation(q.operationId, { label: t("quick.run.retrying"), done: t("quick.run.retryDone"), onDone: () => void invalidate("runs", "run", "rulesHealth") });
    },
    onError: (e) => toast.error(t("quick.run.retryFailed"), { description: e.problem?.detail ?? e.problem?.title }),
  });
}

const STEP_ICON: Record<RunStep["kind"], LucideIcon> = {
  event: MessageCircle,
  conditions: Filter,
  ai: Bot,
  approval: ShieldCheck,
  action: Zap,
  provider: Send,
  retry: RotateCw,
};
const STATUS_STYLE: Record<RunStep["status"], { dot: string; tone: Tone; Icon: LucideIcon }> = {
  ok: { dot: "bg-ok text-white", tone: "ok", Icon: CheckCircle2 },
  failed: { dot: "bg-bad-solid text-white", tone: "bad", Icon: AlertTriangle },
  pending: { dot: "bg-warn-soft text-warn", tone: "warn", Icon: Clock },
  skipped: { dot: "bg-surface-3 text-ink-3", tone: "neutral", Icon: CircleDashed },
  info: { dot: "bg-accent-soft text-accent-ink", tone: "accent", Icon: CircleDashed },
};

/** event → conditions → AI → approval → action → provider response (→ retries), as a vertical stepper. */
export function RunTimeline({ run }: { run: RunDetail }) {
  const { t } = useT();
  return (
    <ol className="relative space-y-1" data-testid="run-timeline">
      {run.timeline.map((s, i) => {
        const Icon = STEP_ICON[s.kind];
        const st = STATUS_STYLE[s.status];
        const last = i === run.timeline.length - 1;
        const labels = s.kind === "ai" ? (s.data?.labels as RunDetail["labels"]) : null;
        const trace = s.kind === "ai" ? (s.data?.traceUrl as string | null | undefined) : null;
        const key = s.kind === "provider" ? (s.data?.idempotencyKey as string | undefined) : undefined;
        // the failed action step reads like the headline: localized reason by code, raw text only when it adds something
        const typed = s.kind === "action" && s.status === "failed" ? runErrorInfo({ error: s.detail, errorCode: (s.data?.errorCode as string | undefined) ?? run.errorCode ?? null }, t) : null;
        const detail = typed ? [typed.message, typed.detail].filter(Boolean).join(": ") : s.detail;
        return (
          <motion.li
            key={`${s.kind}-${i}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="relative flex gap-3 pb-3"
            data-step={s.kind}
            data-status={s.status}
          >
            {!last && <span className="absolute start-[15px] top-8 bottom-0 w-px bg-line" aria-hidden />}
            <span className={cn("relative z-10 grid size-8 shrink-0 place-items-center rounded-full ring-4 ring-surface", st.dot)}>
              <Icon className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[13px] font-semibold text-ink">{t(`quick.run.steps.${s.kind}`)}</span>
                <Badge size="xs" tone={st.tone}>
                  {t(`quick.run.stepStatus.${s.status}`)}
                </Badge>
                {s.at && (
                  <time dateTime={s.at} title={fmtDateTime(s.at)} className="ms-auto text-[11px] text-ink-3">
                    {fmtRelative(s.at)}
                  </time>
                )}
              </div>
              <div className="mt-0.5 text-[12.5px] text-ink-2" dir="auto">
                <span className="font-medium text-ink-2">{s.title}</span>
              </div>
              {detail && (
                <p className={cn("mt-1 rounded-control px-2.5 py-1.5 text-[12.5px] leading-relaxed", s.status === "failed" ? "bg-bad-soft text-bad" : "bg-surface-2 text-ink-2")} dir="auto" data-testid={typed ? "run-action-error" : undefined}>
                  {detail}
                </p>
              )}
              {labels && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  <IntentChip intent={labels.intent} confidence={labels.confidence} size="xs" />
                  <SentimentChip sentiment={labels.sentiment} size="xs" />
                </div>
              )}
              {(trace || key) && (
                // staff only: the AI trace link and the send's idempotency key (ALP-84, no internals for customers)
                <TechnicalDetails className="mt-1.5">
                  {trace && (
                    <a href={trace} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-[12px] font-medium text-accent hover:underline">
                      {t("quick.run.trace")}
                      <ExternalLink className="size-3" />
                    </a>
                  )}
                  {key && (
                    <div className="mt-1 text-[11px] text-ink-3">
                      {t("quick.run.idempotencyKey")}:{" "}
                      <code dir="ltr" className="break-all font-mono">
                        {key}
                      </code>
                    </div>
                  )}
                </TechnicalDetails>
              )}
            </div>
          </motion.li>
        );
      })}
    </ol>
  );
}

export function RunQuickPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useT();
  const { open } = usePeek();
  const q = useApiQuery("run", { params: { runId: id } });
  const retry = useRetryRun();
  const run = q.data;
  const failure = run && run.result === "failed" ? runErrorInfo(run, t) : null;
  const EventIcon = run?.event.type === "message" ? MessagesSquare : MessageCircle;
  return (
    <QuickPanel
      open
      onOpenChange={(v) => !v && onClose()}
      testId="run-panel"
      icon={<Workflow />}
      loading={q.isPending}
      title={run ? (run.ruleName ?? t("automation.runs.deleted")) : t("quick.run.title")}
      subtitle={
        run ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge size="xs" tone={RESULT_TONE[run.result]}>
              {t(`automation.runs.results.${run.result}`)}
            </Badge>
            <time dateTime={run.createdAt}>{fmtDateTime(run.createdAt)}</time>
            {run.retries.length > 0 && <span>· {t("quick.run.retries", { count: run.retries.length })}</span>}
          </span>
        ) : undefined
      }
      actions={
        run?.retriable ? (
          <Button size="sm" variant="primary" icon={<RotateCw />} loading={retry.isPending} data-testid="run-retry" onClick={() => retry.mutate(retryArgs(run.id))}>
            {t("quick.run.retry")}
          </Button>
        ) : undefined
      }
      fullPage={run?.ruleId ? { to: "/automation/runs", search: { ruleId: run.ruleId }, label: t("automation.runs.title") } : { to: "/automation/runs" }}
    >
      {q.isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 rounded-control" />
          ))}
        </div>
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
      ) : run ? (
        <>
          {failure && (
            <div role="alert" className="mb-4 flex items-start gap-2 rounded-field bg-bad-soft px-3 py-2.5 text-[12.5px] text-bad" data-testid="run-error" data-code={run.errorCode ?? undefined}>
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                <strong className="font-semibold">{t("quick.run.error")}: </strong>
                {failure.message}
                {failure.detail && <span className="mt-0.5 block text-[12px] text-ink-2" dir="auto">{failure.detail}</span>}
              </span>
            </div>
          )}
          <PanelSection title={t("quick.run.steps.event")}>
            <div className="flex gap-3 rounded-field border border-line p-3">
              {run.post && <PostThumb post={run.post} size={56} onOpen={() => open("post", run.post!.postId)} />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[12px] text-ink-3">
                  <EventIcon className="size-3.5" />
                  {run.event.type === "message" ? t("automation.runs.eventMessage") : t("automation.runs.eventComment")}
                  {run.event.author && (
                    <PersonLink contactId={null} username={run.event.author} className="font-medium text-ink-2" testId="run-person">
                      <span dir="ltr">&lrm;@{run.event.author}</span>
                    </PersonLink>
                  )}
                </div>
                <p className="mt-1 text-[13.5px] leading-relaxed text-ink" dir="auto">
                  {run.event.text ?? "—"}
                </p>
                <RunCommentStatus eventRef={run.event.type === "comment" && run.event.id ? `comment:${run.event.id}` : ""} variant="full" className="mt-2" />
                {run.event.id && run.event.type === "comment" && (
                  <Link to="/inbox" search={{ id: run.event.id } as never} className="mt-1.5 inline-flex text-[12px] font-medium text-accent hover:underline">
                    {t("quick.run.openEvent")}
                  </Link>
                )}
              </div>
            </div>
          </PanelSection>
          <PanelSection title={t("quick.run.timeline")}>
            <RunTimeline run={run} />
          </PanelSection>
        </>
      ) : null}
    </QuickPanel>
  );
}
